// Fontes — a tela que a PoC não tinha.
//
// Existe porque o princípio 5 fez da procedência uma entidade: coleção sem
// fonte com licença E atribuição registradas **não publica**, e a trava é do
// banco (`publicavel` é coluna gerada). Sem esta tela, a única forma de
// destravar uma publicação seria `UPDATE` manual.
//
// `contem_dado_pessoal` e `exige_contorno` são declarações explícitas, não
// inferências: o CAR traz identificação de proprietário, e há fonte que só se
// obtém driblando bloqueio de portal (ADR-008). Registrar é o que permite
// decidir depois.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { alterar, enviar, obter, remover, type Fonte } from "../api.js";
import { Carregando, Falha, Gaveta, Vazio } from "../pecas/basicos.js";
import { Busca, casa } from "../pecas/Busca.js";
import { Confirmar } from "../pecas/Confirmar.js";
import { NovaFonte } from "./NovaFonte.js";

export function Fontes({ avisar }: { avisar: (t: string, erro?: boolean) => void }): ReactNode {
  const [aberta, setAberta] = useState<Fonte | null>(null);
  const [criando, setCriando] = useState(false);
  const [apagando, setApagando] = useState<Fonte | null>(null);
  const [busca, setBusca] = useState("");
  const qc = useQueryClient();
  const fontes = useQuery({ queryKey: ["fontes"], queryFn: () => obter<Fonte[]>("/fontes") });

  const salvar = useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      alterar<Fonte>(`/fontes/${id}`, corpo),
    onSuccess: (f) => {
      avisar(f.publicavel ? "Fonte agora é publicável." : "Fonte salva — ainda não publicável.");
      setAberta(null);
      void qc.invalidateQueries({ queryKey: ["fontes"] });
      void qc.invalidateQueries({ queryKey: ["camadas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const criar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => enviar<Fonte>("/fontes", corpo),
    onSuccess: (f) => {
      avisar(
        f.publicavel
          ? `Fonte "${f.orgao}" criada e publicável.`
          : `Fonte "${f.orgao}" criada — sem licença, ainda não publicável.`,
      );
      setCriando(false);
      void qc.invalidateQueries({ queryKey: ["fontes"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const apagar = useMutation({
    mutationFn: (id: string) => remover<void>(`/fontes/${id}`),
    onSuccess: () => {
      avisar("Fonte apagada.");
      setApagando(null);
      void qc.invalidateQueries({ queryKey: ["fontes"] });
    },
    // A recusa por uso é informação, não falha: a mensagem diz quantas camadas
    // dependem dela e por onde começar.
    onError: (e: Error) => avisar(e.message, true),
  });

  if (fontes.isLoading) return <Carregando />;
  if (fontes.error) return <Falha erro={fontes.error} />;
  const todas = fontes.data ?? [];
  const lista = todas.filter((f) => casa(`${f.orgao} ${f.url_oficial} ${f.licenca ?? ""}`, busca));
  // Fonte privada não entra na conta: ela é publicável por natureza, e
  // contá-la mandaria o curador procurar uma licença que não existe.
  //
  // `!== "privada"` e não `=== "publica"`, de propósito: durante um rollout a
  // interface nova pode falar com um back office que ainda não devolve
  // `natureza`, e `=== "publica"` esconderia o aviso em TODAS as fontes. Assim
  // a ausência degrada para o lado seguro, que é o mesmo default do banco.
  const semLicenca = lista.filter((f) => f.natureza !== "privada" && !f.publicavel).length;

  return (
    <>
      {/* A ação primária da tela fica à direita, na barra própria. Estava
          dentro do `.filtros`, que é o container dos chips — alinhada à
          esquerda como se fosse mais um filtro. */}
      <div className="barra-tela">
        <Busca
          valor={busca}
          aoMudar={setBusca}
          rotulo="Buscar órgão ou licença"
          achados={lista.length}
          total={todas.length}
        />
        <button className="btn btn-primario" onClick={() => setCriando(true)}>
          Nova fonte…
        </button>
      </div>

      {semLicenca > 0 ? (
        <div className="aviso" role="status">
          <b>
            {semLicenca} de {lista.length} fontes sem licença ou atribuição
          </b>
          Camadas dessas fontes podem ser curadas, mas não publicadas. Asseverar licença sem
          verificar é o risco que o ADR-008 existe para evitar — o preenchimento aqui é declaração,
          não palpite.
        </div>
      ) : null}

      {lista.length === 0 ? (
        <Vazio>
          Nenhuma fonte cadastrada. Comece por aqui: sem fonte não há camada, e sem camada não há o
          que curar nem publicar.
        </Vazio>
      ) : (
        <div className="rolagem">
          <table className="tabela">
            <thead>
              <tr>
                <th>Órgão</th>
                <th>Licença</th>
                <th>Atribuição</th>
                <th>Sinalizações</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((f) => (
                <tr key={f.id}>
                  <td>
                    {f.orgao}
                    <div className="discreto mono">{f.url_oficial}</div>
                  </td>
                  <td className="mono">
                    {f.licenca ?? <span style={{ color: "var(--agd)" }}>a verificar</span>}
                  </td>
                  <td className="mono">
                    {f.atribuicao ?? <span style={{ color: "var(--agd)" }}>a verificar</span>}
                  </td>
                  <td>
                    {f.contem_dado_pessoal ? (
                      <span className="pilula pilula-erro">dado pessoal</span>
                    ) : null}{" "}
                    {f.exige_contorno ? (
                      <span className="pilula pilula-aguardando">exige contorno</span>
                    ) : null}
                    {f.natureza === "privada" ? (
                      <span className="pilula pilula-aguardando">privada</span>
                    ) : null}
                    {f.publicavel ? (
                      <span className="pilula pilula-publicado">publicável</span>
                    ) : null}
                  </td>
                  <td>
                    <div className="linha-botoes">
                      <button className="btn btn-pequeno" onClick={() => setAberta(f)}>
                        Editar
                      </button>
                      <button className="btn btn-pequeno btn-perigo" onClick={() => setApagando(f)}>
                        Apagar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {apagando ? (
        <Confirmar
          titulo={`Apagar ${apagando.orgao}`}
          rotuloAcao="Apagar"
          perigo
          descricao={
            <>
              <p>
                A fonte é apagada de verdade — não há nada abaixo dela. Se alguma camada a usar, a
                exclusão é recusada dizendo quantas são.
              </p>
              <p>Isso não se desfaz.</p>
            </>
          }
          aoConfirmar={() => apagar.mutate(apagando.id)}
          aoCancelar={() => setApagando(null)}
          pendente={apagar.isPending}
        />
      ) : null}

      {criando ? (
        <NovaFonte
          aoFechar={() => setCriando(false)}
          aoSalvar={(corpo) => criar.mutate(corpo)}
          salvando={criar.isPending}
        />
      ) : null}
      {aberta ? (
        <FormularioFonte
          fonte={aberta}
          aoFechar={() => setAberta(null)}
          aoSalvar={(corpo) => salvar.mutate({ id: aberta.id, corpo })}
        />
      ) : null}
    </>
  );
}

function FormularioFonte({
  fonte,
  aoFechar,
  aoSalvar,
}: {
  fonte: Fonte;
  aoFechar: () => void;
  aoSalvar: (corpo: Record<string, unknown>) => void;
}): ReactNode {
  const [f, setF] = useState(fonte);
  // Espelho da coluna gerada `core.fontes.publicavel`. São DUAS CÓPIAS da mesma
  // regra, e a divergência entre elas é silenciosa: a tela diria "publicável" e
  // o banco recusaria (ou o contrário). Quando a regra mudar no banco, muda
  // aqui — foi o que aconteceu com a natureza, em 2026-09-20.
  const publicavel = f.natureza === "privada" || Boolean(f.licenca?.trim() && f.atribuicao?.trim());

  return (
    <Gaveta titulo={fonte.orgao} subtitulo={fonte.url_oficial} aoFechar={aoFechar}>
      {/* Órgão e URL eram os DOIS campos que a gaveta não mostrava — e são
          exatamente os que distinguem uma fonte da outra. Em produção há seis
          fontes chamadas `CAR` e vinte e sete chamadas `INCRA`: abrir qualquer
          uma dava o mesmo título e nenhuma forma de saber qual era, nem de
          corrigir. Pior: o rótulo herdado da PoC está ERRADO em pelo menos dois
          casos (uma fonte `INCRA` aponta para a FUNAI), e sem campo não havia
          como consertar pela tela. */}
      <div className="campo">
        <label htmlFor="orgao">Órgão</label>
        <input id="orgao" value={f.orgao} onChange={(e) => setF({ ...f, orgao: e.target.value })} />
        <p className="campo-ajuda">
          Como o órgão é creditado. Vai para a atribuição no metadado do cliente — confira contra a
          URL abaixo, não contra o nome herdado.
        </p>
      </div>
      <div className="campo">
        <label htmlFor="url_oficial">URL oficial</label>
        <input
          id="url_oficial"
          value={f.url_oficial ?? ""}
          placeholder="https://…"
          onChange={(e) => setF({ ...f, url_oficial: e.target.value })}
        />
        <p className="campo-ajuda">
          O endereço de onde o dado vem. É o que identifica a fonte quando o nome se repete.
        </p>
      </div>
      <div className="campo">
        <label htmlFor="natureza">Natureza</label>
        <select
          id="natureza"
          value={f.natureza}
          onChange={(e) => setF({ ...f, natureza: e.target.value as Fonte["natureza"] })}
        >
          <option value="publica">Pública — base de terceiro redistribuída</option>
          <option value="privada">Privada — achado de cliente hospedado aqui</option>
        </select>
        <p className="campo-ajuda">
          Fonte pública exige licença e atribuição para publicar. A privada não: o achado volta para
          o próprio cliente, e não há redistribuição a terceiro. O default é pública, de propósito —
          esquecer de declarar mantém a exigência.
        </p>
      </div>
      <div className="campo">
        <label htmlFor="licenca">Licença</label>
        <input
          id="licenca"
          value={f.licenca ?? ""}
          placeholder="ex.: CC-BY-4.0, Dados Abertos, Termo de uso do órgão"
          onChange={(e) => setF({ ...f, licenca: e.target.value })}
        />
      </div>
      <div className="campo">
        <label htmlFor="atribuicao">Atribuição</label>
        <input
          id="atribuicao"
          value={f.atribuicao ?? ""}
          placeholder="como o crédito deve aparecer para o cliente"
          onChange={(e) => setF({ ...f, atribuicao: e.target.value })}
        />
      </div>
      <div className="campo">
        <label htmlFor="restricao">Restrição de uso</label>
        <textarea
          id="restricao"
          rows={3}
          value={f.restricao_uso ?? ""}
          onChange={(e) => setF({ ...f, restricao_uso: e.target.value })}
        />
      </div>
      <div className="campo">
        <label htmlFor="periodicidade">Periodicidade oficial</label>
        <input
          id="periodicidade"
          value={f.periodicidade_oficial ?? ""}
          placeholder="ex.: mensal, anual, contínua"
          onChange={(e) => setF({ ...f, periodicidade_oficial: e.target.value })}
        />
      </div>

      <label style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input
          type="checkbox"
          checked={f.contem_dado_pessoal}
          onChange={(e) => setF({ ...f, contem_dado_pessoal: e.target.checked })}
        />
        Contém dado pessoal (LGPD — atributos serão suprimidos na publicação)
      </label>
      <label style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={f.exige_contorno}
          onChange={(e) => setF({ ...f, exige_contorno: e.target.checked })}
        />
        Exige contorno de bloqueio para ser obtida
      </label>

      <div className={`aviso ${publicavel ? "aviso-ok" : ""}`}>
        <div>
          <b style={{ color: publicavel ? "var(--pub)" : "var(--agd)" }}>
            {publicavel ? "Publicável" : "Não publicável"}
          </b>
          <p>
            {f.natureza === "privada"
              ? "Fonte privada é publicável por natureza — não há licença de redistribuição a declarar."
              : publicavel
                ? "Licença e atribuição preenchidas — coleções desta fonte podem ser publicadas."
                : "Faltam licença e/ou atribuição. O banco recusa apontar coleção para versão desta fonte."}
          </p>
        </div>
      </div>

      <div className="linha-botoes">
        <button
          className="btn btn-primario"
          onClick={() =>
            aoSalvar({
              orgao: f.orgao,
              url_oficial: f.url_oficial || null,
              licenca: f.licenca || null,
              atribuicao: f.atribuicao || null,
              restricao_uso: f.restricao_uso || null,
              periodicidade_oficial: f.periodicidade_oficial || null,
              contem_dado_pessoal: f.contem_dado_pessoal,
              exige_contorno: f.exige_contorno,
              natureza: f.natureza,
            })
          }
        >
          Salvar
        </button>
        <button className="btn" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Gaveta>
  );
}
